import React, { useContext, useEffect, useReducer, useState } from "react";
import openSocket from "../../services/socket-io";
import {
  Button,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from "@material-ui/core";
import { DeleteOutline, Edit } from "@material-ui/icons";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import ExtensionModal from "../../components/ExtensionModal";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles
  },
  summaryCard: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    minWidth: 130,
    padding: "6px 12px",
    borderRadius: 10,
    border: "1px solid rgba(15, 118, 110, 0.25)",
    background: "linear-gradient(180deg, rgba(20, 184, 166, 0.14) 0%, rgba(15, 118, 110, 0.08) 100%)",
    marginRight: 12
  },
  summaryLabel: {
    fontSize: 11,
    lineHeight: 1.2,
    color: "#115e59",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.4
  },
  summaryValue: {
    fontSize: 20,
    lineHeight: 1.1,
    color: "#0f172a",
    fontWeight: 700
  },
  zoiperHint: {
    margin: "4px 12px 8px",
    color: "#334155"
  }
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_EXTENSIONS") {
    const extensions = action.payload;
    const newExtensions = [];

    extensions.forEach(extension => {
      const extensionIndex = state.findIndex(q => q.id === extension.id);
      if (extensionIndex !== -1) {
        state[extensionIndex] = extension;
      } else {
        newExtensions.push(extension);
      }
    });

    return [...state, ...newExtensions].sort((a, b) =>
      String(a.extension).localeCompare(String(b.extension))
    );
  }

  if (action.type === "UPSERT_EXTENSION") {
    const extension = action.payload;
    const extensionIndex = state.findIndex(u => u.id === extension.id);

    if (extensionIndex !== -1) {
      state[extensionIndex] = extension;
      return [...state].sort((a, b) =>
        String(a.extension).localeCompare(String(b.extension))
      );
    }

    return [extension, ...state].sort((a, b) =>
      String(a.extension).localeCompare(String(b.extension))
    );
  }

  if (action.type === "DELETE_EXTENSION") {
    const extensionId = action.payload;
    const extensionIndex = state.findIndex(q => q.id === extensionId);
    if (extensionIndex !== -1) {
      state.splice(extensionIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const Extensions = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [extensions, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [extensionModalOpen, setExtensionModalOpen] = useState(false);
  const [selectedExtension, setSelectedExtension] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [summary, setSummary] = useState({ used: 0, limit: null });
  const [sipStatusByUserId, setSipStatusByUserId] = useState({});

  const normalizeSipStateLabel = value => {
    const state = String(value || "").toLowerCase();
    if (state === "online" || state === "avail" || state === "available") {
      return i18n.t("extensions.table.sipOnline");
    }
    if (state === "unavailable") {
      return i18n.t("extensions.table.sipUnavailable");
    }
    if (state === "nonqual") {
      return i18n.t("extensions.table.sipUnqualified");
    }
    if (state) {
      return state;
    }
    return i18n.t("extensions.table.sipOffline");
  };

  const loadSummaryAndStatuses = async () => {
    try {
      const [summaryResponse, registrationsResponse] = await Promise.all([
        api.get("/extensions/summary"),
        api.get("/users/sip/registrations")
      ]);

      setSummary({
        used: Number(summaryResponse.data?.used || 0),
        limit:
          summaryResponse.data?.limit === null ||
          typeof summaryResponse.data?.limit === "undefined"
            ? null
            : Number(summaryResponse.data.limit)
      });

      const statusMap = {};
      (registrationsResponse.data?.registrations || []).forEach(item => {
        statusMap[Number(item.userId)] = {
          registered: Boolean(item.registered),
          state: normalizeSipStateLabel(item.state)
        };
      });
      setSipStatusByUserId(statusMap);
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/extensions");
        dispatch({ type: "LOAD_EXTENSIONS", payload: data });
        await loadSummaryAndStatuses();
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const socket = openSocket();

    socket.on("extension", data => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPSERT_EXTENSION", payload: data.extension });
        loadSummaryAndStatuses();
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_EXTENSION", payload: data.extensionId });
        loadSummaryAndStatuses();
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleOpenExtensionModal = () => {
    setExtensionModalOpen(true);
    setSelectedExtension(null);
  };

  const handleCloseExtensionModal = () => {
    setExtensionModalOpen(false);
    setSelectedExtension(null);
  };

  const handleEditExtension = extension => {
    setSelectedExtension(extension);
    setExtensionModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedExtension(null);
  };

  const handleDeleteExtension = async extensionId => {
    try {
      await api.delete(`/extensions/${extensionId}`);
      dispatch({ type: "DELETE_EXTENSION", payload: extensionId });
      await loadSummaryAndStatuses();
      toast.success(i18n.t("extensions.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setConfirmModalOpen(false);
    setSelectedExtension(null);
  };

  const buildZoiperUser = (companyId, extension) => {
    const normalizedCompanyId = String(companyId || "").replace(/\D/g, "");
    const normalizedExtension = String(extension || "").replace(/[^0-9A-Za-z.-]/g, "");

    if (!normalizedCompanyId || !normalizedExtension) {
      return "-";
    }

    return `c${normalizedCompanyId}-${normalizedExtension}`;
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          selectedExtension &&
          `${i18n.t("extensions.confirmationModal.deleteTitle")} ${selectedExtension.extension}?`
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeleteExtension(selectedExtension.id)}
      >
        {i18n.t("extensions.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <ExtensionModal
        open={extensionModalOpen}
        onClose={handleCloseExtensionModal}
        extensionId={selectedExtension?.id}
        companyId={user?.companyId}
      />
      <MainHeader>
        <Title>{i18n.t("extensions.title")}</Title>
        <MainHeaderButtonsWrapper>
          <div className={classes.summaryCard}>
            <span className={classes.summaryLabel}>{i18n.t("extensions.summary.label")}</span>
            <span className={classes.summaryValue}>
              {summary.used}/{summary.limit === null ? i18n.t("extensions.summary.unlimited") : summary.limit}
            </span>
          </div>
          <Button variant="contained" color="primary" onClick={handleOpenExtensionModal}>
            {i18n.t("extensions.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Typography variant="body2" className={classes.zoiperHint}>
          {i18n.t("extensions.zoiperHint", {
            defaultValue: "Login no Zoiper: c{empresa}-{ramal}. Exemplo: c2-1001."
          })}
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">{i18n.t("extensions.table.extension")}</TableCell>
              <TableCell align="center">
                {i18n.t("extensions.table.zoiperUser", { defaultValue: "Login Zoiper" })}
              </TableCell>
              <TableCell align="center">{i18n.t("extensions.table.label")}</TableCell>
              <TableCell align="center">{i18n.t("extensions.table.user")}</TableCell>
              <TableCell align="center">{i18n.t("extensions.table.status")}</TableCell>
              <TableCell align="center">{i18n.t("extensions.table.sip")}</TableCell>
              <TableCell align="center">{i18n.t("extensions.table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {extensions.map(extension => (
                <TableRow key={extension.id}>
                  <TableCell align="center">{extension.extension}</TableCell>
                  <TableCell align="center">
                    {buildZoiperUser(extension.companyId || user?.companyId, extension.extension)}
                  </TableCell>
                  <TableCell align="center">{extension.label || "-"}</TableCell>
                  <TableCell align="center">{extension.user?.name || i18n.t("extensions.table.unassigned")}</TableCell>
                  <TableCell align="center">
                    {extension.isActive
                      ? i18n.t("extensions.table.active")
                      : i18n.t("extensions.table.inactive")}
                  </TableCell>
                  <TableCell align="center">
                    {extension.userId
                      ? sipStatusByUserId[Number(extension.userId)]?.registered
                        ? i18n.t("extensions.table.sipOnline")
                        : sipStatusByUserId[Number(extension.userId)]?.state || i18n.t("extensions.table.sipOffline")
                      : "-"}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleEditExtension(extension)}>
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedExtension(extension);
                        setConfirmModalOpen(true);
                      }}
                    >
                      <DeleteOutline />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={7} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Extensions;


